import { useCallback, useEffect, useMemo, useRef } from "react";
import { LIVE_SUBSCRIPTION_STATUSES } from "@intelligems/ig-types/dtos/organization-subscriptions";
import * as Sentry from "@sentry/react";

import { useGetBillingSubscriptionAccess } from "@src/api/billing-modules/subscription-access";
import { APPROVAL_RETURN_POLL_INTERVAL_MS } from "./approvalReturn";
import { useGetModularLivePrices } from "@src/api/intelligems/get-live-prices";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { shopifyBillingSettingsUrl } from "@src/utils/shopify";
import type {
  BillingFailureReason,
  BillingManagementViewModel,
} from "../types";
import {
  buildBillingCatalog,
  currentCadence,
  hasModularModulePrices,
  unaccountedSubscriptionPriceIds,
} from "./catalog";
import { PRODUCT_VERSION_2026 } from "./constants";
import { buildBillingSnapshot } from "./snapshot";
import type { BillingCatalog, BillingCycle, BillingSnapshot } from "./types";

type BillingWorkflowSnapshotState = Readonly<{
  catalog: BillingCatalog | null;
  failureReason: BillingFailureReason | null;
  invalidateSubscriptionAccess: () => void;
  isRawSnapshotFetching: boolean;
  management: BillingManagementViewModel;
  ordersPerMonth: number;
  orgId: string;
  rawSnapshot: BillingSnapshot | null;
  refetch: () => void;
  shopId: string | null;
  status: "disabled" | "loading" | "error" | "ready";
  stripeSubscriptionId: string | null;
}>;

const LIVE_MANAGED_STATUSES = new Set<string>([
  ...LIVE_SUBSCRIPTION_STATUSES,
  "paused",
]);

const isLiveManagedStatus = (status: string | null | undefined): boolean =>
  status !== null && status !== undefined && LIVE_MANAGED_STATUSES.has(status);

export const useBillingWorkflowSnapshot = ({
  enabled,
  pollForApprovalReturn,
}: Readonly<{
  enabled: boolean;
  // While a Blocking Approval return is armed the websocket cannot be relied
  // on — its push lands while the merchant is still on Shopify's domain — so
  // activation is discovered by polling this read model instead.
  pollForApprovalReturn: boolean;
}>): BillingWorkflowSnapshotState => {
  const { data: organizationSummary } = useGetOrganizationSummary();
  const subscriptionAccess = useGetBillingSubscriptionAccess({
    enabled,
    refetchInterval: pollForApprovalReturn
      ? APPROVAL_RETURN_POLL_INTERVAL_MS
      : false,
  });
  const livePrices = useGetModularLivePrices({ enabled });
  const org = organizationSummary.org;

  const refetch = useCallback(() => {
    if (!enabled) {
      return;
    }
    void subscriptionAccess.refetch();
    void livePrices.refetch();
  }, [enabled, livePrices, subscriptionAccess]);

  // useQuery returns a new result object on every render but keeps `refetch`
  // stable, and callers hold this callback in effect dependencies.
  const refetchSubscriptionAccess = subscriptionAccess.refetch;
  const invalidateSubscriptionAccess = useCallback(() => {
    if (enabled) {
      void refetchSubscriptionAccess();
    }
  }, [enabled, refetchSubscriptionAccess]);

  const rawSnapshot = useMemo(
    () =>
      subscriptionAccess.data && livePrices.data?.prices
        ? buildBillingSnapshot({
            subscriptionAccessData: subscriptionAccess.data,
            livePrices: livePrices.data.prices,
            stripeSubscriptionSummaries: org.stripeSubscriptionSummaries,
          })
        : null,
    [
      livePrices.data?.prices,
      org.stripeSubscriptionSummaries,
      subscriptionAccess.data,
    ]
  );

  // The cadence the whole screen is built at, so it must be the same one the planner's
  // baseline reads. Matching `status === "active"` alone made an annual `trialing` or
  // `past_due` org render monthly prices while their subscription stayed annual — and the
  // planner then read the resulting submit as an annual→monthly cadence change and
  // re-priced every retained item. See `currentLiveSubscription`.
  const cadence: BillingCycle = rawSnapshot
    ? currentCadence(rawSnapshot)
    : "month";

  const catalog = useMemo(
    () => (rawSnapshot ? buildBillingCatalog(rawSnapshot, cadence) : null),
    [cadence, rawSnapshot]
  );

  const isShopifyManaged =
    org.stripeSubscriptionSummaries.some(
      ({ billingProvider, status }) =>
        billingProvider === "shopify" && isLiveManagedStatus(status)
    ) ||
    org.organizationSubscriptions.some(
      ({ billingProvider, status }) =>
        billingProvider === "shopify" && isLiveManagedStatus(status)
    );

  const stripeSummary = org.stripeSubscriptionSummaries.find(
    ({ billingProvider, status, stripeSubscriptionId }) =>
      billingProvider === "stripe" &&
      stripeSubscriptionId != null &&
      isLiveManagedStatus(status)
  );

  const isStripeManaged =
    !isShopifyManaged &&
    (stripeSummary !== undefined ||
      org.organizationSubscriptions.some(
        ({ billingProvider, status }) =>
          billingProvider === "stripe" && isLiveManagedStatus(status)
      ));

  const fallbackStripeSubscriptionId = isStripeManaged
    ? (rawSnapshot?.subscriptionAccessData.subscriptions.active.find(
        ({ billingProvider, status, cancelAt }) =>
          billingProvider === "stripe" && status === "active" && !cancelAt
      )?.stripeSubscriptionId ??
      rawSnapshot?.subscriptionAccessData.subscriptions.active.find(
        ({ status, cancelAt }) => status === "active" && !cancelAt
      )?.stripeSubscriptionId ??
      rawSnapshot?.subscriptionAccessData.subscriptions.active[0]
        ?.stripeSubscriptionId ??
      null)
    : null;

  const management: BillingManagementViewModel =
    isShopifyManaged && org.shopId
      ? { type: "shopify", url: shopifyBillingSettingsUrl(org.shopId) }
      : isStripeManaged
        ? { type: "stripe" }
        : { type: "none" };

  const isLivePricesCatalogUnusable =
    rawSnapshot !== null && !hasModularModulePrices(rawSnapshot.livePrices);

  const status: BillingWorkflowSnapshotState["status"] = !enabled
    ? "disabled"
    : subscriptionAccess.isError || livePrices.isError
      ? "error"
      : subscriptionAccess.isPending || livePrices.isPending || !rawSnapshot
        ? "loading"
        : isLivePricesCatalogUnusable
          ? "error"
          : "ready";

  const failureReason: BillingFailureReason | null =
    status !== "error"
      ? null
      : isLivePricesCatalogUnusable
        ? "empty-catalog"
        : "query";

  useEffect(() => {
    if (failureReason !== "empty-catalog" || !rawSnapshot) {
      return;
    }

    Sentry.captureMessage("Billing modular price catalog is empty", {
      level: "error",
      extra: {
        orgId: org.id,
        pricesReceived: rawSnapshot.livePrices.length,
        modularPrices: rawSnapshot.livePrices.filter(
          ({ product }) => product.version === PRODUCT_VERSION_2026
        ).length,
        productVersions: [
          ...new Set(
            rawSnapshot.livePrices.map(({ product }) => product.version)
          ),
        ],
        subscriptionAccessWarnings:
          rawSnapshot.subscriptionAccessData.warnings ?? [],
      },
    });
  }, [failureReason, org.id, rawSnapshot]);

  const reportedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!rawSnapshot || !catalog) {
      return;
    }
    // Only a modular org can be the subject of this finding. The catalog is built from
    // `2026_module_v1` prices alone, so a legacy org's subscription prices are unaccounted
    // by construction — every one of them, on every load. Those orgs are bypassed to the
    // legacy panel by the gate and never reach the planner refusal this report mirrors, so
    // reporting them is a false positive that buries the modular findings it exists to surface.
    if (!rawSnapshot.subscriptionAccessData.org.isModular) {
      return;
    }

    const unaccountedBySubscription =
      rawSnapshot.subscriptionAccessData.subscriptions.active.flatMap(
        (subscription) => {
          const priceIds = unaccountedSubscriptionPriceIds(
            rawSnapshot,
            catalog,
            subscription
          );

          return priceIds.length > 0
            ? [
                {
                  stripeSubscriptionId: subscription.stripeSubscriptionId,
                  unaccountedPriceIds: priceIds,
                },
              ]
            : [];
        }
      );
    if (unaccountedBySubscription.length === 0) {
      return;
    }

    const fingerprint = JSON.stringify(unaccountedBySubscription);
    if (reportedRef.current === fingerprint) {
      return;
    }
    reportedRef.current = fingerprint;

    Sentry.captureMessage(
      "Billing subscription carries prices absent from the module catalog",
      {
        level: "warning",
        extra: {
          orgId: org.id,
          unaccountedBySubscription,
          subscriptionAccessWarnings:
            rawSnapshot.subscriptionAccessData.warnings ?? [],
        },
      }
    );
  }, [catalog, org.id, rawSnapshot]);

  return {
    catalog,
    failureReason,
    invalidateSubscriptionAccess,
    isRawSnapshotFetching: subscriptionAccess.isFetching,
    management,
    ordersPerMonth: org.usages.ordersPerMonth,
    orgId: org.id,
    rawSnapshot,
    refetch,
    shopId: org.shopId ?? null,
    status,
    stripeSubscriptionId:
      stripeSummary?.stripeSubscriptionId ?? fallbackStripeSubscriptionId,
  };
};
